import {getPixel, setPixel} from "./tools";
import {IGreyPalette} from "./palette";

export const matrix5 = {
  m1: [
    [0, -.075, -.1, -.075, 0],
    [-.1, -.15, -.2, -.15, -.1],
    [-.1, -.2, 3.5, -.2, -.1],
    [-.1, -.15, -.2, -.15, -.1],
    [0, -.075, -.1, -.075, 0]
  ],
  null:[
    [0,0,0,0,0],
    [0,0,0,0,0],
    [0,0,1,0,0],
    [0,0,0,0,0],
    [0,0,0,0,0],
  ],
  m3_xl: [
    [0,0,0,0,0],
    [0,-1,-1,-1,0],
    [0,-1,9,-1,0],
    [0,-1,-1,-1,0],
    [0,0,0,0,0],
  ],
  m3_cross: [
    [0,0,0,0,0],
    [0,0,-1,0,0],
    [0,-1,5,-1,0],
    [0,0,-1,0,0],
    [0,0,0,0,0],
  ],
  m3_s: [
    [0,0,0,0,0],
    [0,-.25, -.5, -.25,0],
    [0,-.5, 4, -.5,0],
    [0,-.25, -.5, -.25,0],
    [0,0,0,0,0],
  ],
  m3_sx: [
    [0,0,0,0,0],
    [0,-.125,-.25,-.125,0],
    [0,-.25,2.5,-.25,0],
    [0,-.125,-.25,-.125,0],
    [0,0,0,0,0],
  ],
  blur_r: [
    [0,0,1/36,0,0],
    [0,2/36,4/36,2/36,0],
    [1/36,4/36,9/36,4/36,1/36],
    [0,2/36,4/36,2/36,0],
    [0,0,1/36,0,0],
  ],
  blur: [
    [.04,.04,.03,.04,.04],
    [.04,.04,.03,.04,.04],
    [.03,.03,.0,.03,.03],
    [.04,.04,.03,.04,.04],
    [.04,.04,.03,.04,.04],
  ],
}

export const difs = {
  null: [0, 0, 0, 0],
  full: [.3, .2, .3, .2],
  lite: [.15, .1, .15, .1],
  floyd: [7 / 16, 3 / 16, 5 / 16, 1 / 16],
  sierra_lite: [.5, .25, .25, 0]
};

export const rgb_bw = {
  average: [.333, .333, .333],
  luma: [.299, .587, .114],
  srgb: [.2126, .7152, .0722],
  red: [1, 0, 0],
  green: [0, 1, 0],
  blue: [0, 0, 1]
}

export const applyMatrix = (img: ImageData, matrix: number[][]) => {
  const {width, height} = img;
  const W = matrix.length;
  const dW = Math.round((W - 1) / 2);
  const H = matrix[0].length;
  const dH = Math.round((H - 1) / 2);
  const _img: ImageData = new ImageData(width, height);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let sum_r = 0;
      let sum_g = 0;
      let sum_b = 0;
      for (let ky = -dW; ky <= dW; ky += 1) {
        for (let kx = -dH; kx <= dH; kx += 1) {
          const _x = (x + kx < 0 || x + kx > width - 1) ? x : x + kx;
          const _y = (y + ky < 0 || y + ky > height - 1) ? y : y + ky;
          const pixel = getPixel(img, {x: _x, y: _y});
          let val_r = pixel.r * matrix[ky + dW][kx + dH];
          sum_r += val_r;

          let val_g = pixel.g * matrix[ky + dW][kx + dH];
          sum_g += val_g;

          let val_b = pixel.b * matrix[ky + dW][kx + dH];
          sum_b += val_b;
        }
      }
      const clamp_max = 255;
      const clamp_min = 0;
      sum_r = sum_r > clamp_max ? 255 : sum_r < clamp_min ? 0 : sum_r;
      sum_g = sum_g > clamp_max ? 255 : sum_g < clamp_min ? 0 : sum_g;
      sum_b = sum_b > clamp_max ? 255 : sum_b < clamp_min ? 0 : sum_b;
      setPixel(_img, {x, y}, {r: sum_r, g: sum_g, b: sum_b});
    }
  }
  return _img;
}

export const resizeImg = (img: ImageData, width: number = 64, height: number = 64) => {
  const _img: ImageData = new ImageData(width, height);

  for (let x = 0; x < width; x += 1) {
    for (let y = 0; y < height; y += 1) {
      const X0 = Math.round(x * img.width / _img.width);
      const Y0 = Math.round(y * img.height / _img.height);
      const X1 = Math.round((x + 1) * img.width / _img.width);
      const Y1 = Math.round((y + 1) * img.height / _img.height);

      let _r = 0;
      let _g = 0;
      let _b = 0;
      let _n = 0;

      for (let w = X0; w < X1; w += 1) {
        for (let h = Y0; h < Y1; h += 1) {
          const {r, g, b} = getPixel(img, {x: w, y: h});
          _r += r;
          _g += g;
          _b += b;
          _n += 1;
        }
      }

      const pixel = {
        r: Math.ceil(_r / _n),
        g: Math.ceil(_g / _n),
        b: Math.ceil(_b / _n),
      }
      setPixel(_img, {x, y}, pixel);
    }
  }

  return _img;
};

export const greyscale_norm = (img: ImageData, rgb: number[] = rgb_bw.average, levels: any = {}, L: boolean = false, G: boolean = false) => {
  const {width, height} = img;
  const _img: ImageData = new ImageData(width, height);

  let min = 255;
  let max = 0;
  const gamma = Math.pow(2, levels.gamma || 0);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const {r, g, b} = getPixel(img, {x, y});
      let grey = rgb[0] * r + rgb[1] * g + rgb[2] * b;
      min = Math.min(min, grey);
      max = Math.max(max, grey);
      setPixel(_img, {x, y}, {r: grey, g: grey, b: grey});
    }
  }

  const mn = L ? levels.min : min;
  const mx = L ? levels.max : max;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const {r} = getPixel(_img, {x: x, y: y});
      let a = (r - mn) / (mx - mn);
      if (G) {
        a = Math.pow(a, gamma);
      }
      a = Math.round(255 * a);
      a = a < 0 ? 0 : a > 255 ? 255 : a;

      setPixel(_img, {x, y}, {r: a, g: a, b: a});
    }
  }

  return _img;

}

export const apply_grey_palette = (img: ImageData, palette: IGreyPalette[], dif: number[] = difs.null, factors:Record<string, number> = {} ): {
  img: ImageData,
  counts: Record<string, number>,
  schema: any[],
  bright: number,
} => {
  const {width, height} = img;
  const _img: ImageData = new ImageData(width, height);
  const schema = [];
  const counts: Record<string, number> = {};
  let bright = 0;

  let diffusion = new Array(width);
  for (let i = 0; i < diffusion.length; i += 1) {
    diffusion[i] = new Array(height).fill(0);
  }

  for (let y = 0; y < height; y += 1) {
    const row = [];
    for (let x = 0; x < width; x += 1) {
      const {g} = getPixel(img, {x, y});
      let target = g + diffusion[x][y];// + Math.random() * 0 - 0;
      if (target < 0) target = 0;
      if (target > 255) target = 255;

      let i = 0;
      let R = 255;
      palette.forEach((item, index) => {
        const _R = Math.abs(item.br - target);
        const rand = Math.random();
        if (_R < R) {
          if (
            (!factors[index] || factors[index] > rand) &&
            (!factors[index] || (!counts[index] || counts[index] < 1160))
          ) {
            i = index;
            R = _R;
          }

        }
      });

      const delta = target - palette[i].br;
      bright += palette[i].br;

      if (x < width - 1) {
        diffusion[x + 1][y] = diffusion[x + 1][y] + delta * dif[0];
      }
      if (x > 1 && y < height - 1) {
        diffusion[x - 1][y + 1] = diffusion[x - 1][y + 1] + delta * dif[1];
      }
      if (y < height - 1) {
        diffusion[x][y + 1] = diffusion[x][y + 1] + delta * dif[2];
      }
      if (x > 1 && y > 1) {
        diffusion[x - 1][y - 1] = diffusion[x - 1][y - 1] + delta * dif[3];
      }

      setPixel(_img, {x, y}, palette[i].color);
      const id = palette[i].id;
      counts[id] = counts[id] ? counts[id] + 1 : 1;
      row.push(id);
    }
    schema.push(row);
  }
  bright = bright / 4096;
  return {img: _img, counts, schema, bright};
}
